import queryString from 'query-string';
import { useEffect } from 'react';
import { useQuery } from 'react-query';

import { companyAPI } from 'src/core/api/axios';
import { QuerySuspenseBase } from 'src/core/common/components/QuerySuspenseBase/QuerySuspenseBase';
import { Spinner } from 'src/core/common/components/Spinner/Spinner';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { apiUrl } from 'src/core/utils/api';
import { logger } from 'src/utils/datadog-log-wrapper';

import { AffidavitSignature } from './AffidavitSignature';

export const AffidavitSignatureContainer = () => {
  const companyId = useCompanyId();
  const { paymentId, callbackUrl, reason } = queryString.parse(
    window.location.search,
  );

  const redirectToCallbackUrl = (success: boolean) => {
    window.location.href = `${callbackUrl}?success=${success}`;
  };

  const handleSignSuccess = async () => {
    try {
      await companyAPI.post(
        `/missing-receipt/${paymentId}/affidavit/signed`,
        {},
        {
          companyId,
        },
      );
      redirectToCallbackUrl(true);
    } catch {
      redirectToCallbackUrl(false);
    }
  };
  const handleSignError = () => redirectToCallbackUrl(false);

  const missingReceiptQuery = useMissingReceiptQuery({
    paymentId,
    reason,
  });

  useEffect(() => {
    logger.info('Mounting the AffidavitSignatureContainer', {
      href: window.location.href,
      team: 'travel-and-expenses',
      scope: 'affidavit-signature',
    });
  }, []);

  return (
    <QuerySuspenseBase
      queryState={missingReceiptQuery}
      loading={
        <div className="fixed !inset-0 col-span-full row-span-full h-full min-h-[100vh]">
          <div className="flex h-full w-full items-center justify-center">
            <Spinner />
          </div>
        </div>
      }
      fallback={() => {
        redirectToCallbackUrl(false);
        return null;
      }}
    >
      {(missingReceipt) => {
        const signingUrl = missingReceipt.missingReceipt.affidavit.signatureUrl;
        logger.info('Rendering the AffidavitSignatureContainer', {
          paymentId,
          callbackUrl,
          reason,
          signingUrl,
          team: 'travel-and-expenses',
          scope: 'affidavit-signature',
        });
        if (!signingUrl) {
          redirectToCallbackUrl(false);
          return null;
        }
        return (
          <AffidavitSignature
            signingUrl={signingUrl}
            onSignSuccess={() => {
              logger.info('HelloSign client signed', {
                href: window.location.href,
                team: 'travel-and-expenses',
                scope: 'affidavit-signature',
              });
              handleSignSuccess();
            }}
            onSignError={(error) => {
              logger.warn('HelloSign client error', {
                href: window.location.href,
                team: 'travel-and-expenses',
                scope: 'affidavit-signature',
                error,
              });
              handleSignError();
            }}
          />
        );
      }}
    </QuerySuspenseBase>
  );
};

const useMissingReceiptQuery = ({
  paymentId,
  reason,
}: {
  paymentId: string | string[] | null | undefined;
  reason: string | string[] | null | undefined;
}) => {
  const company = useCompany();
  return useQuery(
    ['missing-receipt', paymentId],
    async (): Promise<{
      missingReceipt: { affidavit: { signatureUrl: string } };
    }> => {
      const response = await fetch(
        apiUrl(`/missing-receipt/${paymentId}`, company.id),
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },

          body: JSON.stringify({
            type: 'affidavit',
            reason,
          }),
        },
      );
      if (!response.ok) {
        throw new Error('Failed to post the missing receipt');
      }
      return response.json();
    },
    {
      enabled: true,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
};
